import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useThemeMode } from '../contexts/ThemeContext';
import { supabase } from '../supabaseClient';
import { 
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  IconButton,
  Box,
  useTheme,
  Typography,
  Tooltip,
  Zoom,
  Avatar,
  Button,
  Badge
} from '@mui/material';
import Notifications from './Notifications';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import StarIcon from '@mui/icons-material/Star';

import CollectionsIcon from '@mui/icons-material/Collections';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import EditIcon from '@mui/icons-material/Edit';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import ShareIcon from '@mui/icons-material/Share';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';

import { styled, keyframes } from '@mui/material/styles';

const slideIn = keyframes`
  0% {
    opacity: 0;
    transform: translate3d(-20px, 0, 0);
  }
  100% {
    opacity: 1;
    transform: translate3d(0, 0, 0);
  }
`;

const DrawerHeader = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  padding: theme.spacing(0, 1),
  ...theme.mixins.toolbar,
  justifyContent: 'space-between',
  backgroundColor: theme.palette.background.default,
  borderBottom: `1px solid ${theme.palette.divider}`,
  position: 'sticky',
  top: 0,
  zIndex: theme.zIndex.drawer + 1,
  backdropFilter: 'blur(8px)',
  '& .MuiIconButton-root': {
    color: theme.palette.mode === 'dark' ? '#FFFFFF' : '#000000',
    '&:hover': {
      backgroundColor: theme.palette.mode === 'dark' 
        ? 'rgba(255, 255, 255, 0.08)' 
        : 'rgba(0, 0, 0, 0.04)',
    },
  }
}));

const StyledDrawer = styled(Drawer)(({ theme, open }) => ({
  '& .MuiDrawer-paper': {
    width: { xs: open ? '180px' : '80px', sm: open ? '320px' : '80px' },
    height: '100%',
    position: 'fixed',
    top: { xs: 56, sm: 0 },
    boxSizing: 'border-box',
    backgroundColor: theme.palette.mode === 'dark' ? 'rgba(26, 32, 53, 0.95)' : 'rgba(255, 255, 255, 0.95)',
    borderRight: `1px solid ${theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.08)'}`,
    backdropFilter: 'blur(8px)',
    transition: theme.transitions.create(
      ['width', 'margin', 'background-color', 'box-shadow'],
      {
        easing: theme.transitions.easing.sharp,
        duration: theme.transitions.duration.enteringScreen,
      }
    ),
    overflowX: 'hidden',
    overflowY: 'auto',
    padding: theme.breakpoints.down('sm') ? '12px 8px' : '20px 12px',
    borderRadius: theme.breakpoints.down('sm') ? '0' : (open ? '0 24px 24px 0' : '0'),
    transform: 'translateZ(0)',
    backfaceVisibility: 'hidden',
    willChange: 'width, transform',
    position: 'fixed',
    height: '100%',
    zIndex: theme.zIndex.drawer + (theme.breakpoints.down('sm') ? 2 : 0),
    boxShadow: theme.palette.mode === 'dark' 
      ? '4px 0 24px rgba(0, 0, 0, 0.2)' 
      : '4px 0 24px rgba(0, 0, 0, 0.1)',
  }
}));

const StyledListItemButton = styled(ListItemButton)(({ theme, active }) => ({
  margin: theme.breakpoints.down('sm') ? '6px 10px' : '10px 14px',
  borderRadius: '12px',
  padding: theme.breakpoints.down('sm') ? '14px 18px' : '16px 20px',
  position: 'relative',
  isolation: 'isolate',
  overflow: 'hidden',
  transform: 'translateZ(0)',
  transition: theme.transitions.create(
    ['background-color', 'box-shadow', 'transform'],
    {
      duration: theme.transitions.duration.standard,
      easing: theme.transitions.easing.easeInOut,
    }
  ),
  backgroundColor: active ? 
    theme.palette.mode === 'dark' ? 'rgba(44, 62, 115, 0.25)' : 'rgba(44, 62, 115, 0.08)' 
    : 'transparent',
  '&::before': {
    content: '""',
    position: 'absolute',
    inset: 0,
    backgroundColor: theme.palette.mode === 'dark' ? 'rgba(44, 62, 115, 0.15)' : 'rgba(44, 62, 115, 0.08)',
    opacity: 0,
    transition: theme.transitions.create('opacity', {
      duration: theme.transitions.duration.standard,
      easing: theme.transitions.easing.easeInOut,
    }),
    zIndex: -1,
  },
  '&:hover': {
    transform: 'translateY(-1px)',
    '&::before': {
      opacity: 1,
    }
  },
  '& .MuiListItemIcon-root': {
    transition: 'color 200ms cubic-bezier(0.4, 0, 0.2, 1)',
  },
  ...(active && {
    backgroundColor: theme.palette.mode === 'dark' ? 
      'rgba(44, 62, 115, 0.25)' : 'rgba(44, 62, 115, 0.12)',
    color: theme.palette.mode === 'dark' ? '#fff' : 'rgba(0, 0, 0, 0.87)',
    boxShadow: theme.breakpoints.down('sm') ? 'none' : 
      theme.palette.mode === 'dark' ? 
        '0 4px 20px rgba(44, 62, 115, 0.3)' : 
        '0 4px 20px rgba(44, 62, 115, 0.15)',
    '&:hover': {
      backgroundColor: theme.palette.mode === 'dark' ? 
        'rgba(44, 62, 115, 0.35)' : 'rgba(44, 62, 115, 0.16)',
      transform: 'translateY(-2px)',
    },
    '& .MuiTypography-root': {
      fontWeight: 600,
      color: theme.palette.mode === 'dark' ? '#fff' : 'rgba(0, 0, 0, 0.87)',
    },
    '& .MuiListItemIcon-root': {
      color: theme.palette.mode === 'dark' ? '#fff' : 'rgba(0, 0, 0, 0.87)',
    }
  }),
}));

const StyledListItemIcon = styled(ListItemIcon)(({ theme, active }) => ({
  minWidth: 40,
  transition: theme.transitions.create(['transform', 'color'], {
    duration: theme.transitions.duration.shorter,
    easing: theme.transitions.easing.easeInOut,
  }),
  transform: 'translateZ(0)',
  willChange: 'transform, color',
  color: active
    ? theme.palette.mode === 'dark' ? '#FFFFFF' : '#000000'
    : theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.7)' : 'rgba(0, 0, 0, 0.7)',
  '&:hover': {
    transform: 'scale(1.1) translateZ(0)',
  }
}));

const Sidebar = ({ open, setOpen, handleDrawerClose, activeTab, handleTabChange, isAdmin, session, onEditProfile, setAuthOpen }) => {
  const navigate = useNavigate();
  const theme = useTheme();

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (window.innerWidth <= theme.breakpoints.values.sm) {
        if (open && 
            !event.target.closest('.MuiDrawer-paper') && 
            !event.target.closest('[data-testid="MenuIcon"]')) {
          setOpen(false);
        }
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [open, setOpen, theme.breakpoints.values.sm]);
  
  const { mode, toggleTheme } = useThemeMode();
  const [hoveredItem, setHoveredItem] = useState(null);
  const [profileData, setProfileData] = useState(null);

  useEffect(() => {
    const fetchProfile = async () => {
      if (session?.user?.id) {
        const { data, error } = await supabase
          .from('profiles')
          .select('name, avatar_url')
          .eq('id', session.user.id)
          .single();
        
        if (!error && data) {
          setProfileData(data);
        }
      }
    };

    fetchProfile();
  }, [session]);

  const menuItems = [
    { text: 'Galerim', icon: <CollectionsIcon />, path: '/gallery' },
    { text: 'İş Akışları', icon: <AutoFixHighIcon />, value: 5 },
    ...(session ? [{ text: 'Gösterge Paneli', icon: <StarIcon />, path: '/dashboard' }] : []),
    { text: 'Sohbet', icon: <ChatBubbleOutlineIcon />, path: '/chat' },
    { text: 'Paylaşımlarım', icon: <ShareIcon />, path: '/shared-links' },
    { text: 'Abonelik', icon: <StarIcon />, path: '/subscription' },
  ];

  if (isAdmin) {
    menuItems.push({ 
      text: 'Yönetici', 
      icon: <AdminPanelSettingsIcon />, 
      path: '/admin'
    });
  }

  return (
    <StyledDrawer
      variant={theme.breakpoints.down('sm') ? 'temporary' : 'permanent'}
      open={open}
      onClose={() => setOpen(false)}
    >
      <DrawerHeader>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            width: '100%',
            padding: '20px 24px',
            gap: 2,
            transition: 'all 0.3s ease',
            background: theme => `radial-gradient(circle at 50% 50%, ${theme.palette.mode === 'dark' ? '#1e2642' : '#f5f8ff'} 0%, ${theme.palette.mode === 'dark' ? '#141b36' : '#ffffff'} 100%)`,
            borderRadius: '12px',
            margin: '0 -12px',
            width: 'calc(100% + 24px)',
            backdropFilter: 'blur(12px)',
            isolation: 'isolate',
            position: 'relative',
            zIndex: 1,
            border: theme => `1px solid ${theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'rgba(44, 62, 115, 0.1)'}`,
            boxShadow: theme => `0 4px 24px ${theme.palette.mode === 'dark' ? 'rgba(0, 0, 0, 0.2)' : 'rgba(0, 0, 0, 0.1)'}`,
            '&::before': {
              content: '""',
              position: 'absolute',
              inset: 0,
              background: theme => `
                radial-gradient(circle at center, ${theme.palette.mode === 'dark' ? 'rgba(44, 62, 115, 0.15)' : 'rgba(44, 62, 115, 0.05)'} 0%, transparent 50%),
                repeating-linear-gradient(45deg, ${theme.palette.mode === 'dark' ? 'rgba(44, 62, 115, 0.08)' : 'rgba(44, 62, 115, 0.04)'} 0px, ${theme.palette.mode === 'dark' ? 'rgba(44, 62, 115, 0.08)' : 'rgba(44, 62, 115, 0.04)'} 2px, transparent 2px, transparent 10px),
                repeating-linear-gradient(-45deg, ${theme.palette.mode === 'dark' ? 'rgba(44, 62, 115, 0.08)' : 'rgba(44, 62, 115, 0.04)'} 0px, ${theme.palette.mode === 'dark' ? 'rgba(44, 62, 115, 0.08)' : 'rgba(44, 62, 115, 0.04)'} 2px, transparent 2px, transparent 10px)
              `,
              backgroundSize: '100% 100%, 20px 20px, 20px 20px',
              animation: 'gradientMove 20s ease-in-out infinite',
              zIndex: -1,
              borderRadius: 'inherit',
              opacity: 0.5,
            },
            '&:hover': {
              transform: 'translateY(-2px)',
              boxShadow: theme => `0 8px 32px ${theme.palette.mode === 'dark' ? 'rgba(0, 0, 0, 0.3)' : 'rgba(0, 0, 0, 0.15)'}`,
            }
          }}
        >
          <Box
            sx={{
              minWidth: 40,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Avatar
              src={profileData?.avatar_url}
              alt={profileData?.name || 'Kullanıcı'}
              sx={{
                width: 56,
                height: 56,
                border: theme => `2px solid ${theme.palette.mode === 'dark' ? 'rgba(44, 62, 115, 0.4)' : 'rgba(44, 62, 115, 0.2)'}`,
                background: theme => `linear-gradient(45deg, #2c3e73, #1e2642)`,
                boxShadow: theme => `0 4px 20px ${theme.palette.mode === 'dark' ? 'rgba(0, 0, 0, 0.4)' : 'rgba(44, 62, 115, 0.3)'}`,
                transition: 'all 0.3s ease',
                '&:hover': {
                  transform: 'scale(1.05) rotate(5deg)',
                  boxShadow: theme => `0 8px 25px ${theme.palette.mode === 'dark' ? 'rgba(0, 0, 0, 0.5)' : 'rgba(44, 62, 115, 0.4)'}`,
                }
              }}
            />
          </Box>
          {open && (
            <Box sx={{ flexGrow: 1 }}>
              <Box sx={{ 
                display: 'flex', 
                alignItems: 'center',
                justifyContent: 'space-between',
                width: '100%',
                cursor: 'pointer',
              }}>
                <Box sx={{ 
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 0.5,
                  minWidth: 0,
                  flex: 1
                }}>
                  <Typography
                    onClick={(e) => {
                      // Easter egg: Secret click pattern detection
                      const now = new Date().getTime();
                      const clickPattern = JSON.parse(localStorage.getItem('nameClickPattern') || '[]');
                      
                      // Store click timestamps to detect pattern
                      if (clickPattern.length > 5) {
                        clickPattern.shift(); // Keep only last 5 clicks
                      }
                      clickPattern.push(now);
                      localStorage.setItem('nameClickPattern', JSON.stringify(clickPattern));
                      
                      // Check for rapid triple-click (clicks within 500ms of each other)
                      if (clickPattern.length >= 3) {
                        const isTripleClick = 
                          (clickPattern[clickPattern.length-1] - clickPattern[clickPattern.length-3]) < 500;
                        
                        if (isTripleClick) {
                          // Show easter egg message
                          const funnyNames = [
                            'Gizli Mimar 🕵️',
                            'Süper Tasarımcı 🦸',
                            'Mimarlık Ninja 🥷',
                            'Çılgın Dekoratör 🤪',
                            'Uyuyan Mühendis 😴'
                          ];
                          const randomName = funnyNames[Math.floor(Math.random() * funnyNames.length)];
                          e.target.innerText = randomName;
                          
                          // Reset after 3 seconds
                          setTimeout(() => {
                            e.target.innerText = profileData?.name || 'Kullanıcı';
                          }, 3000);
                          
                          return; // Don't open profile editor for easter egg
                        }
                      }
                      
                      // Normal behavior
                      session ? onEditProfile() : setAuthOpen(true);
                    }}
                    sx={{
                      fontWeight: 600,
                      fontSize: '1.1rem',
                      color: theme => theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.9)' : 'rgba(0, 0, 0, 0.9)',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      transition: 'all 0.2s ease-in-out',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                      maxWidth: '160px',
                      '&:hover': {
                        color: '#2c3e73',
                        transform: 'translateX(4px)',
                      }
                    }}
                  >
                    {profileData?.name || 'Kullanıcı'}
                    <ExpandMoreIcon sx={{ fontSize: 18, ml: 0.5, opacity: 0.5 }} />
                  </Typography>
                  {session && (
                    <Typography
                      onClick={async () => {
                        await supabase.auth.signOut();
                        window.location.reload();
                      }}
                      sx={{
                        fontSize: '0.9rem',
                        color: theme => theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.7)' : 'rgba(0, 0, 0, 0.6)',
                        cursor: 'pointer',
                        fontWeight: 600,
                        transition: 'all 0.2s ease',
                        '&:hover': {
                          color: '#e63946',
                          transform: 'translateX(4px)',
                        }
                      }}
                    >
                      Çıkış Yap
                    </Typography>
                  )}
                </Box>
                <Box 
                  sx={{ 
                    mr: 1,
                    position: 'relative',
                    transition: 'transform 0.2s ease',
                    '&:hover': {
                      transform: 'translateY(-2px)',
                    }
                  }}
                >
                  <Notifications session={session} />
                </Box>
              </Box>
              <Typography
                variant="body2"
                sx={{
                  color: theme => theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.5)' : 'rgba(0, 0, 0, 0.5)',
                  fontSize: '0.8rem',
                  transition: 'color 0.2s ease-in-out',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  maxWidth: '180px'
                }}
              >
                {session?.user?.email}
              </Typography>
            </Box>
          )}
        </Box>
      </DrawerHeader>
      <List sx={{ pt: 2 }}>
        {menuItems.map((item, index) => (
          <Tooltip
            key={item.text}
            title={open ? '' : item.text}
            placement="right"
            TransitionComponent={Zoom}
          >
            <ListItem 
              disablePadding 
              sx={{ 
                display: 'block',
                animation: `${slideIn} 0.5s cubic-bezier(0.4, 0, 0.2, 1) ${index * 0.05}s both`,
                transformOrigin: 'left center',
              }}
              onMouseEnter={() => setHoveredItem(item.value)}
              onMouseLeave={() => setHoveredItem(null)}
            >
              <StyledListItemButton
                active={
                  item.path 
                    ? window.location.pathname === item.path 
                    : activeTab === item.value
                }
                onClick={() => {
                  if (item.path) {
                    navigate(item.path);
                    handleTabChange(null, null);
                  } else {
                    handleTabChange(null, item.value);
                  }
                }}
                sx={{
                  justifyContent: open ? 'initial' : 'center',
                  px: 2.5,
                }}
              >
                <StyledListItemIcon active={activeTab === item.value}>
                  {item.icon}
                </StyledListItemIcon>
                <ListItemText 
                  primary={item.text}
                  sx={{ 
                    opacity: open ? 1 : 0,
                    transition: theme.transitions.create('opacity'),
                    '& .MuiListItemText-primary': {
                      color: theme.palette.mode === 'dark'
                        ? activeTab === item.value ? '#FFFFFF' : 'rgba(255, 255, 255, 0.7)'
                        : activeTab === item.value ? '#000000' : 'rgba(0, 0, 0, 0.7)',
                      fontWeight: activeTab === item.value ? 600 : 400,
                      transition: 'all 0.2s ease-in-out',
                    }
                  }} 
                />
              </StyledListItemButton>
            </ListItem>
          </Tooltip>
        ))}
      </List>
    </StyledDrawer>
  );
};

export default Sidebar;
